import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  MessageCircle,
  Shield,
  User,
  Image as ImageIcon,
  Video as VideoIcon,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import AuthenticatedMedia from '../components/AuthenticatedMedia';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://backend.rainbitx.com';

const UserChat = () => {
  const isGuest = !localStorage.getItem('token');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingFileType, setPendingFileType] = useState(null); // 'image' | 'video'
  const [pendingFilePreview, setPendingFilePreview] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [chatError, setChatError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const isUserNearBottomRef = useRef(true);

  const scrollToBottom = () => {
    if (isUserNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const threshold = 100;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    isUserNearBottomRef.current = distanceFromBottom <= threshold;
  };

  const messageFormatted = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i, arr) => (
      <React.Fragment key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Get user info from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserInfo(user);
  }, []);

  // Fetch or create chat room
  const fetchOrCreateChatRoom = async () => {
    try {
      setLoading(true);
      setChatError('');
      const token = localStorage.getItem('token');

      if (isGuest) {
        const response = await axios.post(
          `${API_BASE_URL}/api/chat/guest/start`,
          {},
          { withCredentials: true },
        );
        setRoomId(response.data.data.id);
        setUserInfo({ fullName: 'Guest' });
        await fetchMessages(response.data.data.id);
        return;
      }

      // First, try to get existing chat rooms
      const roomsResponse = await axios.get(
        `${API_BASE_URL}/api/chat/my-rooms`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (roomsResponse.data.success && roomsResponse.data.data.length > 0) {
        // Use the first active or most recent room
        const activeRoom =
          roomsResponse.data.data.find((room) => room.status === 'ACTIVE') ||
          roomsResponse.data.data[0];
        setRoomId(activeRoom.id);
        await fetchMessages(activeRoom.id);
      } else {
        // No existing rooms, create a new one
        const createResponse = await axios.post(
          `${API_BASE_URL}/api/chat/start`,
          { subject: 'Customer Support Request' },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (createResponse.data.success) {
          const newRoom = createResponse.data.data;
          setRoomId(newRoom.id);
          setLoading(false);
          toast.success('Chat started! You can now send messages.');
        }
      }
    } catch (error) {
      setChatError('Customer Service could not start. Please check the connection and retry.');
      toast.error('Customer Service could not start. Please retry.');
      setLoading(false);
    }
  };

  // Fetch messages
  const fetchMessages = async (chatRoomId) => {
    try {
      const token = localStorage.getItem('token');
      if (isGuest) {
        const response = await axios.get(
          `${API_BASE_URL}/api/chat/guest/messages`,
          { withCredentials: true },
        );
        if (response.data.success) setMessages(response.data.data.messages);
        setLoading(false);
        return;
      }
      const response = await axios.get(
        `${API_BASE_URL}/api/chat/${chatRoomId}/messages`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        const newMessages = response.data.data.messages;
        setMessages((prev) => {
          // Only update if messages actually changed to avoid unnecessary re-renders
          if (
            JSON.stringify(prev.map((m) => m.id)) ===
            JSON.stringify(newMessages.map((m) => m.id))
          ) {
            return prev;
          }
          return newMessages;
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (pendingFile) {
      if (pendingFileType === 'image') await handleSendImage(pendingFile);
      else if (pendingFileType === 'video') await handleSendVideo(pendingFile);
      return;
    }

    if (!newMessage.trim()) return;
    if (!roomId) {
      toast.error('Chat room not ready. Please wait...');
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('token');

      if (isGuest) {
        const response = await axios.post(
          `${API_BASE_URL}/api/chat/guest/message`,
          { message: newMessage },
          { withCredentials: true },
        );
        if (response.data.success) {
          setNewMessage('');
          setMessages((prev) => [...prev, response.data.data]);
        }
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/chat/${roomId}/message`,
        { message: newMessage },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        setNewMessage('');
        // Add the new message to the list immediately for better UX
        const newMsg = response.data.data;
        setMessages((prev) => [...prev, newMsg]);
        // Auto-scroll to bottom when user sends a message
        isUserNearBottomRef.current = true;
        // Optionally refresh to get any updates
        setTimeout(() => fetchMessages(roomId), 500);
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleImageButtonClick = () => {
    if (!roomId) {
      toast.error('Chat room not ready. Please wait...');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleVideoButtonClick = () => {
    if (!roomId) {
      toast.error('Chat room not ready. Please wait...');
      return;
    }
    videoInputRef.current?.click();
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
    ];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image (JPEG, PNG, GIF, WEBP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should not exceed 5MB');
      return;
    }
    setPendingFile(file);
    setPendingFileType('image');
    setPendingFilePreview(URL.createObjectURL(file));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a valid video file');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video size should not exceed 50MB');
      return;
    }
    setPendingFile(file);
    setPendingFileType('video');
    setPendingFilePreview(URL.createObjectURL(file));
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const clearPendingFile = () => {
    setPendingFile(null);
    setPendingFileType(null);
    setPendingFilePreview(null);
  };

  const handleSendImage = async (file) => {
    if (!file) return;
    if (!roomId) {
      toast.error('Chat room not ready. Please wait...');
      return;
    }

    try {
      setUploadingImage(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post(
        isGuest ? `${API_BASE_URL}/api/chat/guest/send-image` : `${API_BASE_URL}/api/chat/${roomId}/send-image`,
        formData,
        {
          headers: {
            ...(isGuest ? {} : { Authorization: `Bearer ${token}` }),
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
        },
      );

      if (response.data.success) {
        const newMsg = response.data.data;
        setMessages((prev) => [...prev, newMsg]);
        clearPendingFile();
        setTimeout(() => fetchMessages(roomId), 500);
      }
    } catch (error) {
      toast.error('Failed to send image. Please try again.');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSendVideo = async (file) => {
    if (!file) return;
    if (!roomId) {
      toast.error('Chat room not ready. Please wait...');
      return;
    }

    try {
      setUploadingVideo(true);
      setVideoUploadProgress(0);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('video', file);

      const response = await axios.post(
        isGuest ? `${API_BASE_URL}/api/chat/guest/send-video` : `${API_BASE_URL}/api/chat/${roomId}/send-video`,
        formData,
        {
          headers: {
            ...(isGuest ? {} : { Authorization: `Bearer ${token}` }),
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setVideoUploadProgress(percent);
          },
        },
      );

      if (response.data.success) {
        const newMsg = response.data.data;
        setMessages((prev) => [...prev, newMsg]);
        clearPendingFile();
        setTimeout(() => fetchMessages(roomId), 500);
        toast.success('Video sent!');
      }
    } catch (error) {
      toast.error('Failed to send video. Please try again.');
    } finally {
      setUploadingVideo(false);
      setVideoUploadProgress(0);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  // Start polling for new messages
  useEffect(() => {
    if (roomId) {
      // Poll every 3 seconds
      pollingIntervalRef.current = setInterval(() => {
        fetchMessages(roomId);
      }, 3000);

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }
  }, [roomId]);

  // Initialize chat
  useEffect(() => {
    fetchOrCreateChatRoom();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        height: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: '#ffffff',
      }}
    >
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '15px 20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          flexShrink: 0,
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div className='d-flex align-items-center'>
            <Link
              to='/deposit'
              className='text-white me-3'
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ArrowLeft size={24} />
            </Link>
            <div className='flex-grow-1'>
              <h5
                className='mb-0'
                style={{
                  fontSize: 'clamp(1rem, 3vw, 1.2rem)',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <MessageCircle size={20} />
                Live Chat Support
              </h5>
              {userInfo && (
                <small
                  style={{
                    opacity: 0.9,
                    fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                  }}
                >
                  {userInfo.email || `UID: ${userInfo.uid || 'User'}`}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          maxWidth: '1200px',
          width: '100%',
          margin: '0 auto',
        }}
      >
        {/* Messages Area */}
        <div
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            background: '#f8f9fa',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {loading ? (
            <div className='text-center py-5'>
              <Spinner animation='border' variant='primary' />
              <p className='mt-3 text-muted'>Loading chat...</p>
            </div>
          ) : chatError ? (
            <div className='text-center py-5'>
              <MessageCircle size={60} className='text-danger' style={{ opacity: 0.45 }} />
              <p className='mt-3 mb-3 text-danger'>{chatError}</p>
              <Button variant='primary' onClick={fetchOrCreateChatRoom}>Retry</Button>
            </div>
          ) : messages.length === 0 ? (
            <div className='text-center text-muted py-5'>
              <MessageCircle size={60} style={{ opacity: 0.3 }} />
              <p className='mt-3'>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`mb-3 d-flex ${
                  msg.isFromAdmin
                    ? 'justify-content-start'
                    : 'justify-content-end'
                }`}
              >
                <div
                  style={{
                    maxWidth: '70%',
                    padding: '12px 16px',
                    borderRadius: msg.isFromAdmin
                      ? '15px 15px 15px 5px'
                      : '15px 15px 5px 15px',
                    background: msg.isFromAdmin
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : '#e3e3ff',
                    color: msg.isFromAdmin ? 'white' : '#333',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.75rem',
                      marginBottom: '5px',
                      opacity: msg.isFromAdmin ? 0.9 : 0.7,
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {msg.isFromAdmin ? (
                      <>
                        <Shield size={12} />
                        Customer Support
                      </>
                    ) : (
                      <>
                        <User size={12} />
                        {msg.sender?.fullName || userInfo?.fullName || 'You'}
                      </>
                    )}
                  </div>
                  <div style={{ fontSize: '0.95rem', wordWrap: 'break-word' }}>
                    {msg.messageType === 'image' && msg.imageUrl ? (
                      <div style={{ marginTop: '6px' }}>
                        <AuthenticatedMedia
                          src={msg.imageUrl}
                          alt={msg.fileName || 'Uploaded image'}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '320px',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            display: 'block',
                          }}
                          openOnClick
                        />
                        {msg.fileName ? (
                          <div
                            style={{
                              fontSize: '0.7rem',
                              marginTop: '6px',
                              opacity: msg.isFromAdmin ? 0.85 : 0.7,
                            }}
                          >
                            {msg.fileName}
                          </div>
                        ) : null}
                      </div>
                    ) : msg.messageType === 'video' && msg.videoUrl ? (
                      <div style={{ marginTop: '6px' }}>
                        <AuthenticatedMedia
                          as='video'
                          src={msg.videoUrl}
                          showOpenButton
                          controls
                          style={{
                            maxWidth: '100%',
                            maxHeight: '280px',
                            borderRadius: '10px',
                            display: 'block',
                            backgroundColor: '#000',
                          }}
                        />
                        {msg.fileName ? (
                          <div
                            style={{
                              fontSize: '0.7rem',
                              marginTop: '6px',
                              opacity: msg.isFromAdmin ? 0.85 : 0.7,
                            }}
                          >
                            {msg.fileName}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      messageFormatted(msg.message)
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: '0.7rem',
                      marginTop: '5px',
                      opacity: msg.isFromAdmin ? 0.8 : 0.6,
                      textAlign: 'right',
                    }}
                  >
                    {new Date(msg.createdAt).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
        {/* Message Input Box - Fixed at Bottom */}
        <div
          style={{
            background: '#fafafa',
            borderTop: '1px solid #e5e5e5',
            padding: 'clamp(12px, 2vw, 16px) clamp(16px, 3vw, 20px)',
            flexShrink: 0,
          }}
        >
          <Form onSubmit={handleSendMessage}>
            {/* Pending file preview */}
            {pendingFile && (
              <div
                style={{
                  marginBottom: '8px',
                  padding: '8px 10px',
                  background: '#f3f0ff',
                  borderRadius: '10px',
                  border: '1px solid #c4b5fd',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                {pendingFileType === 'image' ? (
                  <img
                    src={pendingFilePreview}
                    alt='preview'
                    style={{
                      height: '56px',
                      width: '56px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <video
                    src={pendingFilePreview}
                    style={{
                      height: '56px',
                      width: '80px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      color: '#4c1d95',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {pendingFile.name}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#7c3aed' }}>
                    {pendingFileType === 'image' ? '🖼️ Image' : '🎥 Video'} ·{' '}
                    {(pendingFile.size / (1024 * 1024)).toFixed(2)} MB
                  </div>
                  <div
                    style={{
                      fontSize: '0.7rem',
                      color: '#6c757d',
                      marginTop: '2px',
                    }}
                  >
                    Click send ➤ to upload
                  </div>
                </div>
                <button
                  type='button'
                  onClick={clearPendingFile}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#ef4444',
                    fontSize: '18px',
                    lineHeight: 1,
                    padding: '0 4px',
                    flexShrink: 0,
                  }}
                  title='Remove'
                >
                  ✕
                </button>
              </div>
            )}
            {/* Video upload progress bar */}
            {uploadingVideo && (
              <div style={{ marginBottom: '8px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    color: '#764ba2',
                    fontWeight: '600',
                    marginBottom: '4px',
                  }}
                >
                  <span>📤 Uploading video...</span>
                  <span>{videoUploadProgress}%</span>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '6px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '3px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${videoUploadProgress}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #667eea, #764ba2)',
                      borderRadius: '3px',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: '0.7rem',
                    color: '#6c757d',
                    marginTop: '3px',
                  }}
                >
                  Large files take longer depending on your internet speed.
                </div>
              </div>
            )}
            <div
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                ref={fileInputRef}
                type='file'
                accept='image/jpeg,image/jpg,image/png,image/gif,image/webp'
                style={{ display: 'none' }}
                onChange={handleImageSelect}
              />
              <input
                ref={videoInputRef}
                type='file'
                accept='.mp4,.webm,.mov,.avi,.mkv,.flv,.wmv,.m4v,.3gp,.mpeg,.mpg'
                style={{ display: 'none' }}
                onChange={handleVideoSelect}
              />

              <Button
                type='button'
                onClick={handleImageButtonClick}
                disabled={
                  loading ||
                  sending ||
                  uploadingImage ||
                  uploadingVideo ||
                  !roomId
                }
                style={{
                  flexShrink: 0,
                  borderRadius: '6px',
                  width: '40px',
                  height: '40px',
                  minWidth: '40px',
                  padding: 0,
                  background: '#ffffff',
                  border: '1px solid #667eea',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'none',
                  marginRight: '6px',
                  cursor:
                    loading ||
                    sending ||
                    uploadingImage ||
                    uploadingVideo ||
                    !roomId
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    loading ||
                    sending ||
                    uploadingImage ||
                    uploadingVideo ||
                    !roomId
                      ? 0.6
                      : 1,
                }}
                title='Upload image'
              >
                {uploadingImage ? (
                  <Spinner animation='border' size='sm' />
                ) : (
                  <ImageIcon size={18} style={{ color: '#667eea' }} />
                )}
              </Button>

              <Button
                type='button'
                onClick={handleVideoButtonClick}
                disabled={
                  loading ||
                  sending ||
                  uploadingImage ||
                  uploadingVideo ||
                  !roomId
                }
                style={{
                  flexShrink: 0,
                  borderRadius: '6px',
                  width: '40px',
                  height: '40px',
                  minWidth: '40px',
                  padding: 0,
                  background: '#ffffff',
                  border: '1px solid #764ba2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'none',
                  marginRight: '6px',
                  cursor:
                    loading ||
                    sending ||
                    uploadingImage ||
                    uploadingVideo ||
                    !roomId
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    loading ||
                    sending ||
                    uploadingImage ||
                    uploadingVideo ||
                    !roomId
                      ? 0.6
                      : 1,
                }}
                title='Upload video (max 50MB)'
              >
                {uploadingVideo ? (
                  <div style={{ textAlign: 'center', lineHeight: 1 }}>
                    <Spinner
                      animation='border'
                      size='sm'
                      style={{ width: '14px', height: '14px' }}
                    />
                    <div
                      style={{
                        fontSize: '9px',
                        color: '#764ba2',
                        marginTop: '2px',
                        fontWeight: '700',
                      }}
                    >
                      {videoUploadProgress}%
                    </div>
                  </div>
                ) : (
                  <VideoIcon size={18} style={{ color: '#764ba2' }} />
                )}
              </Button>

              <Form.Control
                as='textarea'
                rows={1}
                placeholder='Type your message...'
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  // Auto-resize
                  e.target.style.height = 'auto';
                  const newHeight = e.target.value.trim()
                    ? Math.min(e.target.scrollHeight, 120)
                    : 48;
                  e.target.style.height = newHeight + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={
                  sending || uploadingImage || uploadingVideo || loading || !roomId
                }
                style={{
                  borderRadius: '8px',
                  border: '1px solid #dcdcdc',
                  padding: '13px 16px',
                  fontSize: '0.95rem',
                  flex: 1,
                  minWidth: 0,
                  backgroundColor: 'white',
                  outline: 'none',
                  boxShadow: 'none',
                  color: '#333',
                  resize: 'none',
                  overflowY: 'auto',
                  minHeight: '48px',
                  maxHeight: '120px',
                  lineHeight: '1.5',
                }}
                onFocus={(e) => {
                  e.target.style.border = '1px solid #dcdcdc';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <Button
                type='submit'
                disabled={
                  sending ||
                  uploadingImage ||
                  uploadingVideo ||
                  !roomId ||
                  (!newMessage.trim() && !pendingFile)
                }
                style={{
                  flexShrink: 0,
                  borderRadius: '6px',
                  width: '48px',
                  height: '40px',
                  minWidth: '48px',
                  padding: 0,
                  marginLeft: '6px',
                  background: pendingFile
                    ? 'linear-gradient(135deg, #667eea, #764ba2)'
                    : '#8b8ae6',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: pendingFile
                    ? '0 2px 10px rgba(102,126,234,0.5)'
                    : 'none',
                  cursor:
                    sending || (!newMessage.trim() && !pendingFile)
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    sending || (!newMessage.trim() && !pendingFile) ? 0.6 : 1,
                }}
              >
                {sending ? (
                  <Spinner
                    animation='border'
                    size='sm'
                    style={{ color: 'white' }}
                  />
                ) : (
                  <Send size={20} style={{ color: 'white' }} />
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default UserChat;
